/**
 * Shared image + emoji editor for projects and workspaces. Compact: square
 * preview on the left, file picker + emoji input + remove button stacked
 * right. Image takes precedence over emoji when both are set.
 *
 * Image upload contract: caller provides an `uploadImage(file)` Promise that
 * returns a fresh image_key (and the caller persists it). On success we just
 * refresh the parent. We DO NOT optimistically blit the local blob — the
 * server-side immutability comes from content-hash keying, so a brief
 * round-trip is the safer pattern.
 */
import { useRef, useState } from 'react';
import { ProjectAvatar } from './ProjectAvatar';
import { ImageCropModal } from './ImageCropModal';
import { PROJECT_COLORS } from '@/lib/projectColors';
import { cn } from '@/lib/utils';

interface Props {
  name: string;
  image_key: string | null;
  emoji: string | null;
  color: string | null;
  /** Upload the file to the server. Resolves on success. */
  onUploadImage: (file: File) => Promise<void>;
  /** Clear the image_key on the server. */
  onRemoveImage: () => Promise<void>;
  /** Persist the emoji on the server (null = clear). */
  onSaveEmoji: (emoji: string | null) => Promise<void>;
  /** Persist the project color (swatch id, null = clear → uses periwinkle default). */
  onSaveColor?: (color: string | null) => Promise<void>;
  disabled?: boolean;
}

/** Server cap is 4MB. We pre-flight this on the original file picked,
 *  before crop. The cropped/compressed output is well under 4MB so the
 *  upload itself won't trip the cap. Matches ProfileModal's pipeline. */
const MAX_BYTES = 4 * 1024 * 1024;
const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export function IdentityEditor({
  name,
  image_key,
  emoji,
  color,
  onUploadImage,
  onRemoveImage,
  onSaveEmoji,
  onSaveColor,
  disabled,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [emojiDraft, setEmojiDraft] = useState(emoji ?? '');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  /** File picked, awaiting crop confirmation — same staging pattern as ProfileModal. */
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const emojiDirty = (emojiDraft || '') !== (emoji ?? '');

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null);
    if (!ALLOWED.includes(file.type)) {
      setErr(`Use JPG / PNG / WebP / GIF. Got ${file.type || 'unknown'}.`);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    if (file.size > MAX_BYTES) {
      setErr(`Max 4MB. Got ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    // Stage into the crop modal; upload happens on confirm.
    setPendingFile(file);
  }
  async function uploadCropped(blob: Blob) {
    setPendingFile(null);
    setBusy('upload');
    setErr(null);
    try {
      // Wrap Blob → File so the FormData("image", file) helper in api/client
      // gets a real filename (server logs the original name on multipart parse).
      const cropped = new File([blob], 'image.jpg', { type: 'image/jpeg' });
      await onUploadImage(cropped);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }
  async function removeImage() {
    setBusy('remove');
    setErr(null);
    try {
      await onRemoveImage();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(null);
    }
  }
  async function pickColor(value: string | null) {
    if (!onSaveColor) return;
    setBusy('color');
    setErr(null);
    try {
      await onSaveColor(value);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(null);
    }
  }

  /** Treat the stored value as "custom" when it's not one of the known
   *  swatch ids (a raw hex/oklch string from the custom picker). */
  const isCustomColor = color !== null && !PROJECT_COLORS.some((c) => c.id === color);

  async function saveEmoji() {
    const clean = (emojiDraft || '').trim();
    // Reject pure-ASCII — the emoji input should accept emoji, not a stray
    // "P" or "AB" that would render as text in the avatar tile. Allow null
    // (clearing) and any non-ASCII content (covers emoji + ZWJ + flags).
    if (clean !== '' && /^[\x20-\x7E]+$/.test(clean)) {
      setErr('Use an emoji (e.g. 🚀), not letters.');
      return;
    }
    setBusy('emoji');
    setErr(null);
    try {
      await onSaveEmoji(clean === '' ? null : clean);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex items-start gap-4 rounded-lg border border-border bg-background/40 p-4">
      <div className="shrink-0">
        <ProjectAvatar
          name={name}
          image_key={image_key}
          emoji={image_key ? null : emoji}
          color={color}
          size="xl"
          shape="rounded"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept={ALLOWED.join(',')}
            onChange={onPick}
            disabled={disabled || busy !== null}
            className="sr-only"
            id="identity-image-input"
          />
          <label
            htmlFor="identity-image-input"
            aria-busy={busy === 'upload'}
            className="cursor-pointer rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-ring/60 hover:bg-card/80"
          >
            {busy === 'upload'
              ? 'Uploading…'
              : image_key
                ? 'Replace image'
                : 'Upload image'}
          </label>
          {busy === 'upload' && (
            <span
              aria-hidden
              className="size-3 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
            />
          )}
          {image_key && (
            <button
              type="button"
              onClick={removeImage}
              disabled={disabled || busy !== null}
              className="rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-destructive/60 hover:text-destructive disabled:opacity-60"
            >
              {busy === 'remove' ? 'Removing…' : 'Remove'}
            </button>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground">
          JPG / PNG / WebP / GIF · max 4MB · cropped to 500×500.
        </p>
        {/* Emoji section — disabled when an image is set (image overrides
            emoji at render time, so accepting input would silently no-op). */}
        <div className="space-y-1">
          <label
            htmlFor="identity-emoji-input"
            className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
          >
            Or pick an emoji
            {image_key && (
              <span className="ml-2 normal-case tracking-normal text-muted-foreground/70">
                — remove the image first
              </span>
            )}
          </label>
          <div className="flex gap-2">
            <input
              id="identity-emoji-input"
              type="text"
              value={emojiDraft}
              onChange={(e) => setEmojiDraft(e.target.value)}
              maxLength={8}
              placeholder="🚀"
              disabled={disabled || busy !== null || !!image_key}
              className="block w-24 rounded-md border border-border bg-card px-3 py-2 text-center text-lg focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-60"
            />
            <button
              type="button"
              onClick={saveEmoji}
              disabled={!emojiDirty || busy !== null || !!image_key}
              className="rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {busy === 'emoji' ? 'Saving…' : 'Save emoji'}
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Emoji picker:{' '}
            <kbd className="font-mono">Ctrl+Cmd+Space</kbd> (macOS) ·{' '}
            <kbd className="font-mono">Win+.</kbd> (Windows) ·{' '}
            <kbd className="font-mono">Ctrl+.</kbd> (Linux). Up to 8 chars (covers ZWJ sequences
            like 👨‍💻).
          </p>
        </div>
        {/* Project accent color — drives the section glow + input focus ring
            inside this project. Tap a swatch to pick; tap again to clear back
            to the workspace default (periwinkle). */}
        {onSaveColor && (
          <div className="space-y-1.5">
            <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Accent color
            </label>
            <div role="radiogroup" aria-label="Project accent color" className="flex flex-wrap items-center gap-2">
              {PROJECT_COLORS.map((c) => {
                const selected = color === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={c.label}
                    title={c.label}
                    disabled={disabled || busy !== null}
                    onClick={() => pickColor(selected ? null : c.id)}
                    className={cn(
                      'no-glow size-6 rounded-full transition-all duration-150',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                      selected
                        ? 'ring-2 ring-offset-2 ring-offset-background scale-110'
                        : 'opacity-80 hover:opacity-100 hover:scale-105',
                      busy === 'color' && 'opacity-50',
                    )}
                    style={{
                      backgroundColor: c.oklch,
                      ...(selected ? { boxShadow: `0 0 16px -2px ${c.oklch}` } : {}),
                    }}
                  />
                );
              })}
              {/* Custom picker — native color input, returns #RRGGBB which
                  resolves the same as oklch in our color-mix() pipeline.
                  Stored verbatim in projects.color (TEXT column). */}
              <label
                className={cn(
                  'no-glow relative size-6 cursor-pointer rounded-full transition-all duration-150',
                  isCustomColor
                    ? 'ring-2 ring-offset-2 ring-offset-background scale-110'
                    : 'opacity-80 hover:opacity-100 hover:scale-105',
                  (disabled || busy !== null) && 'pointer-events-none opacity-50',
                )}
                title="Custom color"
                style={{
                  background: isCustomColor
                    ? color!
                    : 'conic-gradient(from 0deg, oklch(0.7 0.2 0), oklch(0.78 0.18 60), oklch(0.75 0.16 130), oklch(0.7 0.14 200), oklch(0.65 0.2 280), oklch(0.7 0.2 0))',
                  ...(isCustomColor ? { boxShadow: `0 0 16px -2px ${color}` } : {}),
                }}
              >
                <input
                  type="color"
                  className="no-glow absolute inset-0 size-full cursor-pointer opacity-0"
                  value={isCustomColor ? color! : '#a3c2ff'}
                  onChange={(e) => pickColor(e.target.value)}
                  aria-label="Pick custom color"
                  disabled={disabled || busy !== null}
                />
              </label>
              {color && (
                <button
                  type="button"
                  onClick={() => pickColor(null)}
                  disabled={disabled || busy !== null}
                  className="ml-1 rounded px-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
                  title="Clear color (back to periwinkle default)"
                >
                  clear
                </button>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Drives the section glow + input focus ring inside this project.
            </p>
          </div>
        )}
        {err && (
          <div role="alert" className="text-xs text-destructive">
            {err}
          </div>
        )}
      </div>
      {pendingFile && (
        <ImageCropModal
          file={pendingFile}
          onConfirm={uploadCropped}
          onClose={() => {
            setPendingFile(null);
            if (fileRef.current) fileRef.current.value = '';
          }}
        />
      )}
    </div>
  );
}
